import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, KeyRound, Lock, Mail, Send, ShieldCheck } from "lucide-react";
import ADMIN_PATHS from "../ADMIN_PATHS";
import API from "../../../util/API";

const CORPORATE_FONT_FAMILY = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [step, setStep] = useState("request");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getErrorMessage = (err) => {
    if (!err?.response) {
      if (/timeout|ECONN|Network Error/i.test(String(err?.message || ""))) {
        return "We couldn't reach Cherrfy. Check your connection and try again.";
      }
      return "Something went wrong.";
    }
    if (err.response.status === 429) {
      return "You've tried too often. Wait a few minutes and try again.";
    }
    return (
      err?.response?.data?.error?.message ||
      err?.response?.data?.message ||
      "Something went wrong."
    );
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim().toLowerCase());

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsSubmitting(true);
    try {
      await API.post("/admin/forgot-password", { email });
      setStep("verify");
      setMessage("If an admin account exists for that email, a reset code has been sent.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsSubmitting(true);
    try {
      const res = await API.post("/admin/verify-reset-otp", { email, otp });
      const token = res?.data?.data?.resetToken;
      if (!token) {
        setError("Unexpected response. Please try again.");
        return;
      }
      setResetToken(token);
      setStep("reset");
      setMessage("Code verified. Set your new password.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setMessage("");
    setIsSubmitting(true);
    try {
      await API.post("/admin/reset-password", { resetToken, password });
      setStep("done");
      setMessage("Your admin password has been updated.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    "w-full rounded-md border border-slate-900/20 bg-white px-11 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 " +
    "outline-none transition-all focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10";

  const primaryButton =
    "group flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm font-semibold transition-all ";

  const getButtonTone = (canSubmit) => {
    if (isSubmitting) return "cursor-wait border border-slate-200 bg-slate-100 text-slate-700";
    if (!canSubmit) return "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400";
    return "border border-slate-900 bg-slate-900 text-white hover:bg-black";
  };

  const progressIndex = useMemo(() => {
    if (step === "verify") return 2;
    if (step === "reset" || step === "done") return 3;
    return 1;
  }, [step]);

  const showAlert = Boolean(error || message);
  const alertTitle = error ? "Action needed" : "Update";
  const alertText = error || message;

  return (
    <div className="w-full bg-white" style={{ fontFamily: CORPORATE_FONT_FAMILY }}>
      <div className="mx-auto flex min-h-screen max-w-[1240px] items-center justify-center px-3 py-8 sm:px-4 md:py-12 lg:px-6">
        <div className="w-full max-w-[520px] overflow-hidden rounded-md border border-slate-900/20 bg-white">
          <section className="relative bg-white p-6 sm:p-8 md:p-9">
            <div className="relative">
              <Link
                to={ADMIN_PATHS.AUTH.LOGIN}
                className="mb-6 inline-flex items-center gap-2 text-xs font-semibold text-slate-700 transition-colors hover:text-[#D2042D]"
              >
                <ArrowLeft size={12} />
                Back to login
              </Link>

              <h1
                className="text-[1.9rem] leading-[1.03] text-slate-900"
                style={{ fontFamily: '"Iowan Old Style", "Times New Roman", serif' }}
              >
                Reset your admin password.
              </h1>

              <p className="mt-3 text-sm leading-6 text-slate-600">
                Enter your admin email, verify the code, and choose a new password.
              </p>

              <div className="mt-6 flex items-center gap-3">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className={
                      "h-1.5 flex-1 rounded-full transition-colors " +
                      (progressIndex >= item ? "bg-slate-900" : "bg-slate-200")
                    }
                    aria-hidden="true"
                  />
                ))}
              </div>

              {showAlert && (
                <div
                  role="alert"
                  className={
                    "mt-5 rounded-xl border px-4 py-3 " +
                    (error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700")
                  }
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.13em]">{alertTitle}</p>
                  <p className="mt-1 text-sm font-medium">{alertText}</p>
                </div>
              )}

              {step === "request" && (
                <form className="mt-6 space-y-4" onSubmit={handleRequestCode}>
                  <div>
                    <label htmlFor="admin-forgot-email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                      Admin Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="admin-forgot-email"
                        type="email"
                        className={inputBase}
                        placeholder="admin@cherrfy.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError("");
                          setMessage("");
                        }}
                        autoComplete="username"
                        required
                      />
                    </div>
                    {!isValidEmail(email) && email.trim() && (
                      <p className="mt-2 text-xs font-medium text-slate-600">Invalid email format.</p>
                    )}
                  </div>

                  <button
                    disabled={isSubmitting || !isValidEmail(email)}
                    className={`${primaryButton}${getButtonTone(isValidEmail(email))}`}
                  >
                    {isSubmitting ? (
                      "Sending code..."
                    ) : (
                      <>
                        Send code
                        <Send size={12} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {step === "verify" && (
                <form className="mt-6 space-y-4" onSubmit={handleVerifyOtp}>
                  <div>
                    <label htmlFor="admin-forgot-otp" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                      Verification Code
                    </label>
                    <div className="relative">
                      <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="admin-forgot-otp"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        className={inputBase}
                        placeholder="Enter 6-digit code"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value);
                          setError("");
                          setMessage("");
                        }}
                        autoComplete="one-time-code"
                        required
                      />
                    </div>
                  </div>

                  <button
                    disabled={isSubmitting || !otp.trim()}
                    className={`${primaryButton}${getButtonTone(Boolean(otp.trim()))}`}
                  >
                    {isSubmitting ? (
                      "Verifying..."
                    ) : (
                      <>
                        Verify code
                        <ShieldCheck size={13} className="transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep("request");
                      setOtp("");
                      setError("");
                      setMessage("");
                    }}
                    className="text-xs font-semibold text-slate-900 transition-colors hover:text-[#D2042D]"
                  >
                    Use a different email
                  </button>
                </form>
              )}

              {step === "reset" && (
                <form className="mt-6 space-y-4" onSubmit={handleResetPassword}>
                  <div>
                    <label htmlFor="admin-forgot-password" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="admin-forgot-password"
                        type="password"
                        className={inputBase}
                        placeholder="Create a new password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                          setMessage("");
                        }}
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="admin-forgot-confirm" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="admin-forgot-confirm"
                        type="password"
                        className={inputBase}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setError("");
                          setMessage("");
                        }}
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>

                  <button
                    disabled={isSubmitting || !password || !confirmPassword}
                    className={`${primaryButton}${getButtonTone(Boolean(password && confirmPassword))}`}
                  >
                    {isSubmitting ? (
                      "Updating..."
                    ) : (
                      <>
                        Set new password
                        <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {step === "done" && (
                <div className="mt-6 space-y-4">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-700">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.13em]">Password updated</p>
                    <p className="mt-1 text-sm font-medium">You can now sign in with your new admin password.</p>
                  </div>

                  <Link
                    to={ADMIN_PATHS.AUTH.LOGIN}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-900 bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-black"
                  >
                    Back to login
                    <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              )}

              <div className="mt-6 border-t border-slate-200 pt-4">
                <p className="text-xs font-medium leading-relaxed text-slate-600">
                  Remember your password?{" "}
                  <Link to={ADMIN_PATHS.AUTH.LOGIN} className="font-semibold text-slate-900 transition-colors hover:text-[#D2042D]">
                    Sign in instead
                  </Link>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
